import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deletePromptModule,
  deletePromptModuleTemplate,
  getPromptModule,
  listPromptModuleTemplates,
  putPromptModuleTemplate,
  updatePromptModule,
  type PromptModule,
  type PromptModuleStatus,
  type PromptModuleTemplate,
} from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
  useConfirm,
} from "@/components/Forms";
import { ArrowLeftIcon } from "@/components/Icons";

/**
 * Template names a module can carry. Matches the JAR convention under
 * {@code modules/verticals/src/main/resources/verticals/<vertical>/}.
 * Per-locale rows are stored separately so each (templateName, locale)
 * is editable in isolation.
 */
const TEMPLATE_NAMES = [
  "system_prompt",
  "instant_response",
  "escalation_handoff",
  "disqualification_goodbye",
  "nurture/24h_reminder",
  "nurture/1h_reminder",
  "reactivation/30d",
  "reactivation/60d",
  "reactivation/90d",
] as const;

const LOCALES = ["sv", "en"] as const;

export default function PromptModuleEditorPage() {
  const { moduleId = "" } = useParams<{ moduleId: string }>();
  const { t } = useTranslation();
  const [module, setModule] = useState<PromptModule | null>(null);
  const [templates, setTemplates] = useState<PromptModuleTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);
  const [activeTemplate, setActiveTemplate] = useState<string>("system_prompt");
  const [activeLocale, setActiveLocale] = useState<string>("sv");

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    setError(null);
    Promise.all([getPromptModule(moduleId), listPromptModuleTemplates(moduleId)])
      .then(([m, ts]) => {
        if (cancelled) return;
        setModule(m);
        setTemplates(ts.templates);
      })
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("promptModules.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [moduleId, reloadAt, t]);

  if (error && !module) {
    return (
      <PageContainer title={t("promptModules.title")} subtitle="">
        <ErrorBanner message={error} />
        <Link to="/prompt-modules" className="text-sm text-accent hover:underline">
          {t("promptModules.backToList")}
        </Link>
      </PageContainer>
    );
  }

  if (!module || !templates) {
    return (
      <PageContainer title={t("promptModules.title")} subtitle="">
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={module.name}
      subtitle={`${module.vertical} · ${t("promptModules.title")}`}
      actions={
        <Link
          to="/prompt-modules"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon size={14} /> {t("promptModules.backToList")}
        </Link>
      }
    >
      <ErrorBanner message={error} />

      <div className="grid gap-3 lg:grid-cols-[1fr_18rem]">
        <Card title={t("promptModules.editorTitle")}>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Select
              value={activeTemplate}
              onChange={(e) => setActiveTemplate(e.target.value)}
              className="w-auto"
            >
              {TEMPLATE_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <Select
              value={activeLocale}
              onChange={(e) => setActiveLocale(e.target.value)}
              className="w-auto"
            >
              {LOCALES.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </Select>
            <span className="text-[11px] text-muted-foreground">
              {hasRow(templates, activeTemplate, activeLocale)
                ? t("promptModules.statusOverridden")
                : t("promptModules.statusJarFallback")}
            </span>
          </div>

          <TemplateEditor
            moduleId={module.id}
            templateName={activeTemplate}
            locale={activeLocale}
            initialBody={
              templates.find(
                (r) => r.templateName === activeTemplate && r.locale === activeLocale,
              )?.body ?? ""
            }
            onSaved={() => setReloadAt((n) => n + 1)}
            onDeleted={() => setReloadAt((n) => n + 1)}
          />
        </Card>

        <MetaPanel
          module={module}
          templates={templates}
          onChanged={() => setReloadAt((n) => n + 1)}
        />
      </div>
    </PageContainer>
  );
}

function hasRow(rows: PromptModuleTemplate[], name: string, locale: string): boolean {
  return rows.some((r) => r.templateName === name && r.locale === locale);
}

interface EditorProps {
  moduleId: string;
  templateName: string;
  locale: string;
  initialBody: string;
  onSaved: () => void;
  onDeleted: () => void;
}

function TemplateEditor({
  moduleId,
  templateName,
  locale,
  initialBody,
  onSaved,
  onDeleted,
}: EditorProps) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    setBody(initialBody);
  }, [initialBody, templateName, locale]);

  async function save() {
    if (!body.trim()) {
      setErr(t("promptModules.errorBodyRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await putPromptModuleTemplate(moduleId, templateName, locale, body);
      onSaved();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (
      !(await confirm({
        title: t("promptModules.resetConfirmTitle"),
        body: t("promptModules.resetConfirmBody"),
        confirmLabel: t("promptModules.resetConfirmLabel"),
        destructive: true,
      }))
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await deletePromptModuleTemplate(moduleId, templateName, locale);
      onDeleted();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <ErrorBanner message={err} />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={20}
        spellCheck={false}
        className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-xs leading-relaxed text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
        placeholder={`# verticals/.../${templateName}.${locale}.vm — paste body here`}
      />
      <p className="text-[11px] text-muted-foreground">
        {t("promptModules.editorHint")}
      </p>
      <div className="flex gap-2">
        <Button variant="primary" onClick={save} disabled={busy}>
          {busy ? t("forms.saving") : t("forms.save")}
        </Button>
        <Button variant="destructive" onClick={reset} disabled={busy}>
          {t("promptModules.resetToJar")}
        </Button>
      </div>
    </div>
  );
}

interface MetaProps {
  module: PromptModule;
  templates: PromptModuleTemplate[];
  onChanged: () => void;
}

function MetaPanel({ module, templates, onChanged }: MetaProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(module.name);
  const [description, setDescription] = useState(module.description ?? "");
  const [status, setStatus] = useState<PromptModuleStatus>(module.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirm = useConfirm();

  const overriddenCount = useMemo(() => templates.length, [templates]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await updatePromptModule(module.id, { name, description: description || null, status });
      onChanged();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !(await confirm({
        title: t("promptModules.deleteConfirmTitle"),
        body: t("promptModules.deleteConfirmBody", { name: module.name }),
        confirmLabel: t("promptModules.deleteConfirmLabel"),
        destructive: true,
      }))
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await deletePromptModule(module.id);
      window.location.assign("/admin/prompt-modules");
    } catch (e) {
      setErr(toErrorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Card title={t("promptModules.metaTitle")}>
      <ErrorBanner message={err} />
      <div className="space-y-3">
        <Field label={t("promptModules.fieldName")}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t("promptModules.fieldDescription")}>
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label={t("promptModules.fieldStatus")}>
          <Select value={status} onChange={(e) => setStatus(e.target.value as PromptModuleStatus)}>
            <option value="DRAFT">DRAFT</option>
            <option value="PUBLISHED">PUBLISHED</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </Select>
        </Field>
        <div className="text-[11px] text-muted-foreground">
          {t("promptModules.overriddenCount", { count: overriddenCount })}
        </div>
        <Badge tone={module.status === "PUBLISHED" ? "success" : "neutral"}>{module.status}</Badge>
        <div className="flex gap-2 pt-2">
          <Button variant="primary" onClick={save} disabled={busy}>
            {t("forms.save")}
          </Button>
          <Button variant="destructive" onClick={remove} disabled={busy}>
            {t("forms.delete")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
